import apiV2, { apiV1 } from "api-v2";
import { showToast, processFormData } from "utils/helpers";
import {
	endPerformance,
	logPerformance,
	startPerformance,
} from "utils/logging";

export const getListCompanyData = async (type, params) => {
	try {
		let response = await apiV2.get(`${type}`, { params });
		if (response.status === 200) {
			return response.data.data;
		}
	} catch (e) {
		console.log(e);
		showToast(e?.response?.data?.message, "Error");
	}
};
export const getListProjectExperience = async ({ queryKey, pageParam = 1 }) => {
	const [_, params] = queryKey;
	try {
		const startTime = startPerformance();
		const response = await apiV2.get(`project-experience`, {
			params: { ...params, page: pageParam, per_page: 10 },
		});
		if (response.status === 200) {
			const endTime = endPerformance();
			logPerformance(startTime, endTime, "RFx - getListProjectExperience");
			return response.data.data;
		}
		throw new Error("Failed to fetch project experience");
	} catch (err) {
		throw new Error(
			err?.response?.data?.message || "Failed to fetch project experience"
		);
	}
};

export const getListKeyPersonnels = async ({ queryKey, pageParam = 1 }) => {
	const [_, params] = queryKey;
	try {
		const response = await apiV2.get(`key-personnel`, {
			params: { ...params, page: pageParam, per_page: 10 },
		});
		if (response.status === 200) {
			return response.data.data;
		}
		throw new Error("Failed to fetch key personnel");
	} catch (err) {
		throw new Error(
			err?.response?.data?.message || "Failed to fetch key personnel"
		);
	}
};

export const getPastProjectForRfx = async ({ queryKey }) => {

	const [_, { projectId, rfqId }] = queryKey;

	try {
		const response = await apiV1.get(
			`/past-projects/${projectId}?replacement=1&rfq_id=${rfqId}`
		);

		if (response.status === 200) {
			return response.data.data;
		}

		throw new Error("Failed to fetch past project for RFX");
	} catch (err) {
		throw new Error(
			err?.response?.data?.message || "Failed to fetch past project for RFX"
		);
	}
};

export const getPastKeyPersonnelForRfx = async ({ queryKey }) => {

	const [_, { keyPersonnelId, rfqId }] = queryKey;

	try {
		const response = await apiV1.get(
			`/companies/key-personnel/${keyPersonnelId}?replacement=1&rfq_id=${rfqId}`
		);

		if (response.status === 200) {
			return response.data.data.key_personnel;
		}
		throw new Error("Failed to fetch key personnel for RFX");
	} catch (err) {
		throw new Error(
			err?.response?.data?.message || "Failed to fetch key personnel for RFX"
		);
	}
};

export const saveProjectChangesForRfx = async (projectId, rfxId, values) => {
	try {
		// create a copy of values to avoid modifying the original
		const formValues = { ...values };

		// Add rfxId to the values
		formValues.rfq_id = rfxId;

		// Handle testimonials field - set to null if empty string
		if (formValues.testimonials === "") {
			formValues.testimonials = null;
		}

		// Use the processFormData helper to properly format the form data
		const formData = processFormData(formValues);

		const response = await apiV2.post(
			`/project-experience/${projectId}/save-replacement`,
			formData
		);

		if (response.status === 200) {
			const message =
				response.data?.message ||
				"Project changes saved successfully for this RFX";

			showToast(message, "Success", true);

			return {
				status: true,
				response: {
					message: message,
				},
				data: response.data,
			};
		}
	} catch (err) {
		showToast(
			err?.response?.data?.message || "Failed to save project changes",
			"error"
		);
		throw err;
	}
};

export const saveKeyPersonnelChangesForRfx = async (
	keyPersonnelId,
	rfxId,
	values
) => {
	try {
		const formValues = { ...values };

		// Add rfqId to the values
		formValues.rfq_id = rfxId;

		// We don't need to add roles if it's not in the form
		// Roles handling can be removed since it's not required by the API

		// Handle resume_file field
		if (formValues.resume_file === "" || formValues.resume_file === undefined) {
			formValues.resume_file = null;
		}

		// Handle resume field
		if (formValues.resume === "" || formValues.resume === undefined) {
			formValues.resume = null;
		}

		// Use the processFormData helper to properly format the form data
		const formData = processFormData(formValues);

		const response = await apiV2.post(
			`/key-personnel/${keyPersonnelId}/save-replacement`,
			formData
		);

		if (response.status === 200) {
			showToast(
				response.data.message || "Key personnel saved successfully",
				"Success",
				true
			);
			return response.data;
		}

		throw new Error("Failed to save key personnel changes");
	} catch (err) {
		showToast(
			err?.response?.data?.message || "Failed to save key personnel changes",
			"Error"
		);

		// Return the error for proper handling in the component
		if (err.response && err.response.data) {
			throw err.response.data;
		}
		throw err;
	}
};

export const getListCorporateDocument = async ({ queryKey, pageParam = 1 }) => {
	const [_, params] = queryKey;
	try {
		const response = await apiV2.get(`corporate-document`, {
			params: { ...params, page: pageParam, per_page: 20 },
		});
		if (response.status === 200) {
			return response.data.data;
		}
	} catch (err) {
		throw new Error(
			err?.response?.data?.message || "Failed to fetch corporate documents"
		);
	}
};

export const createProjectExperience = async (data) => {
	const response = await apiV2.post("project-experience", data);
	if (response.status === 200 || response.status === 201) {
		return response.data.data;
	}
	throw new Error("Failed to create project");
};

export const createKeyPersonnel = async (data) => {
	const response = await apiV2.post("key-personnel", data);
	if (response.status === 200 || response.status === 201) {
		return response.data.data;
	}
	throw new Error("Failed to create key personnel");
};

export const createCorporateDocument = async (data) => {
	const formData = new FormData();
	formData.append("file", data.file);
	formData.append("category_id", data.category_id);
	const response = await apiV1.post("/documents", formData, {
		headers: { "Content-Type": "multipart/form-data" },
	});
	if (response.status === 200 || response.status === 201) {
		return response.data.data;
	}
	throw new Error("Failed to upload document");
};

export const getCompanyAdmins = async ({ queryKey }) => {
	const [_, companyId] = queryKey;
	const response = await apiV2.get(`company/${companyId}/admins`);
	if (response.status === 200) {
		return response.data.data?.admins ?? [];
	}
	throw new Error("Failed to fetch company admins");
};

export const notifyCompanyAdmin = async (companyId, payload) => {
	const response = await apiV2.post(`company/${companyId}/admins/notify`, payload);
	if (response.status === 200) {
		return response.data;
	}
	throw new Error("Failed to send notification");
};

export const getPastProjectById = async ({ queryKey }) => {

	const [_, pastProjectId] = queryKey;

	try {
		const response = await apiV1.get(`/past-projects/${pastProjectId}`);

		if (response.status === 200) {
			return response.data.data;
		}

		throw new Error("Failed to fetch past project");
	} catch (err) {
		throw new Error(
			err?.response?.data?.message || "Failed to fetch past project"
		);
	}
};
